import { editorConfig, ACTIONS } from './../config';
import { ICONS_MAP } from './../icons';
import { useMemo } from 'react';

const ACTION_MAP = {
  [ACTIONS.COPY]: { displayName: 'Copy', icon: 'copy_outlined', group: 'context menu' },
  [ACTIONS.DELETE]: { displayName: 'Delete', icon: 'delete_outlined', group: 'context menu' },
  [ACTIONS.CUT]: { displayName: 'Cut', icon: 'cut_outlined', group: 'context menu' },
  [ACTIONS.PARAGRAPH]: { displayName: 'Text', icon: 'text_outlined', group: 'suggestions' },
  [ACTIONS.HEADINGS]: { group: 'suggestions' },
  [ACTIONS.HORIZONTAL_RULE]: { displayName: 'Divider', icon: 'horizontal_rule_outlined', group: 'suggestions' },
  [ACTIONS.IMAGE]: { displayName: 'Image', icon: 'image_outlined', group: 'suggestions' },
  [ACTIONS.BULLET_LIST]: { displayName: 'Bullet List', icon: 'bullet_list_outlined', group: 'suggestions' },
  [ACTIONS.ORDERED_LIST]: { displayName: 'Ordered List', icon: 'ordered_list_outlined', group: 'suggestions' },
  [ACTIONS.TASK_LIST]: { displayName: 'Task List', icon: 'task_list_outlined', group: 'suggestions' },
  [ACTIONS.BLOCKQUOTE]: { displayName: 'Blockquote', icon: 'blockquote_outlined', group: 'suggestions' },
  [ACTIONS.CODE_BLOCK]: { displayName: 'Code Block', icon: 'codeblock_outlined', group: 'suggestions' },
  [ACTIONS.VIDEO]: { displayName: 'Video', icon: 'video_outlined', group: 'suggestions' },
  [ACTIONS.TABLE]: { displayName: 'Table', icon: 'table_outlined', group: 'suggestions' },
  [ACTIONS.DATE]: { displayName: 'Date', icon: 'date_outlined', group: 'suggestions' },
  [ACTIONS.ACCORDION]: { displayName: 'Expand', icon: 'toggle_card_outlined', group: 'suggestions' },
};

const getEnabledShortcutFeatures = (config) => {
  return Object.entries(config.features)
    .filter(([key, feature]) => feature.enabled && feature.shortcuts?.enabled)
    .flatMap(([key, feature]) => {
      if (key === ACTIONS.HEADINGS) {
        return Array.from({ length: 3 }, (_, i) => ({
          key: `${key}_${i + 1}`,
          displayName: `Heading ${i + 1}`,
          keywords: [`h${i + 1}`],
          icon: ICONS_MAP[`heading_${i + 1}_outlined`],
          group: ACTION_MAP[key]?.group || 'format',
          order: feature.shortcuts.order,
        }));
      }

      if (feature?.types && Array.isArray(feature?.types) && feature.types.length > 0) {
        return feature.types.map((type) => ({
          key: `${type.value}_${key}`,
          displayName: type.title,
          keywords: [type.value, String(type.title).toLowerCase()],
          icon: ICONS_MAP[`${type.value}_${key}`],
          group: ACTION_MAP[key]?.group || 'format',
          order: feature.shortcuts.order,
        }));
      }

      return {
        key,
        displayName: ACTION_MAP[key]?.displayName || key,
        icon: ICONS_MAP[ACTION_MAP[key]?.icon] || 'default_icon',
        group: ACTION_MAP[key]?.group || 'other',
        order: feature.shortcuts.order,
      };
    })
    .flat()
    .sort((a, b) => a.order - b.order);
};

const getContextMenuFeatures = () => {
  return [
    {
      key: ACTIONS.COPY,
      displayName: ACTION_MAP[ACTIONS.COPY].displayName,
      icon: ICONS_MAP[ACTION_MAP[ACTIONS.COPY].icon] || 'default_icon',
      group: ACTION_MAP[ACTIONS.COPY].group,
      order: 1,
    },
    {
      key: ACTIONS.DELETE,
      displayName: ACTION_MAP[ACTIONS.DELETE].displayName,
      icon: ICONS_MAP[ACTION_MAP[ACTIONS.DELETE].icon] || 'default_icon',
      group: ACTION_MAP[ACTIONS.DELETE].group,
      order: 2,
    },
    {
      key: ACTIONS.CUT,
      displayName: ACTION_MAP[ACTIONS.CUT].displayName,
      icon: ICONS_MAP[ACTION_MAP[ACTIONS.CUT].icon] || 'default_icon',
      group: ACTION_MAP[ACTIONS.CUT].group,
      order: 3,
    },
  ];
};

const groupByCategory = (items) =>
  items.reduce((acc, item) => {
    if (!acc[item.group]) {
      acc[item.group] = [];
    }
    acc[item.group].push(item);
    return acc;
  }, {});

export const useGetAllShortcutOptions = () => {
  const enabledOptions = useMemo(() => getEnabledShortcutFeatures(editorConfig), []);
  const contextMenuOptions = useMemo(() => getContextMenuFeatures(), []);

  const filterShortcutOptions = (query, includeContextMenu = false) => {
    let result = enabledOptions;

    if (includeContextMenu) {
      const combinedFeatures = [...contextMenuOptions, ...enabledOptions];
      result = combinedFeatures;
    }

    if (query) {
      result = result.filter(({ displayName, key, keywords }) => {
        if (key.toLowerCase().includes(query.toLowerCase()) || displayName.toLowerCase().includes(query.toLowerCase()))
          return true;
        if (keywords && keywords.includes(query.toLowerCase())) return true;
        return false;
      });
    }

    return groupByCategory(result);
  };

  return {
    features: groupByCategory(enabledOptions),
    filterShortcutOptions,
  };
};

export default useGetAllShortcutOptions;
